import { useEffect, useRef, useState } from "react";
import { MessageSquare, X, Send } from "lucide-react";
import { api } from "../lib/api";
import { COMPANY } from "../lib/site";

const SESSION_KEY = "usi_chat_session";

const seedReplies = [
  "Hi 👋 I'm the USI Security assistant. How can we help protect your site today?",
];

const scriptedAnswer = (text) => {
  const t = text.toLowerCase();
  if (t.includes("price") || t.includes("cost") || t.includes("quote"))
    return "Quotes depend on site footprint, hours of cover and tech mix. A consultant can prepare a free, no-obligation proposal within one working day — would you like to share your site details?";
  if (t.includes("k9") || t.includes("dog"))
    return "Our K9 patrols use certified handlers and trained dogs — ideal for construction sites, plant yards and high-risk perimeters. We can deploy nationwide.";
  if (t.includes("cctv") || t.includes("camera") || t.includes("tower"))
    return "We deploy rapid-install CCTV towers, pods and fixed installations, all monitored 24/7 by our UK control room with AI-augmented analytics.";
  if (t.includes("guard") || t.includes("manned"))
    return "All officers are SIA-licensed, DBS-checked and briefed to site-specific assignment instructions. Static, mobile or hybrid deployments available.";
  if (t.includes("hello") || t.includes("hi"))
    return "Hello! Tell me a bit about your site — sector, location and main concern — and I'll point you to the right solution.";
  if (t.includes("urgent") || t.includes("emergency"))
    return "For urgent incidents please call our line on +44 (0) 1268 947 077. The control room can mobilise officers immediately.";
  return "Great question. The best next step is a 10-minute call with one of our consultants — would you like to leave your number, or jump to our quick blueprint quiz?";
};

export const LiveChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => {
    let s = localStorage.getItem(SESSION_KEY);
    if (!s) { s = "ses_" + Math.random().toString(36).slice(2, 12); localStorage.setItem(SESSION_KEY, s); }
    return s;
  });
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (messages.length === 0) {
      setMessages([{ sender: "bot", text: seedReplies[0], id: "s0" }]);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e?.preventDefault();
    if (!input.trim() || sending) return;
    const userText = input.trim();
    setInput("");
    setSending(true);
    const userMsg = { sender: "user", text: userText, id: "u" + Date.now() };
    setMessages((m) => [...m, userMsg]);
    api.post("/chat", { session_id: sessionId, sender: "user", text: userText }).catch(() => {});

    // simulate typing
    setTimeout(() => {
      const botText = scriptedAnswer(userText);
      const botMsg = { sender: "bot", text: botText, id: "b" + Date.now() };
      setMessages((m) => [...m, botMsg]);
      api.post("/chat", { session_id: sessionId, sender: "bot", text: botText }).catch(() => {});
      setSending(false);
    }, 850);
  };

  const quick = (q) => { setInput(q); setTimeout(()=>{ send({ preventDefault(){} }); }, 50); };

  return (
    <>
      {/* Launcher */}
      {!open && (
        <button
          data-testid="chat-launcher"
          onClick={() => setOpen(true)}
          className="fixed bottom-6 right-6 z-50 group"
          aria-label="Open chat"
        >
          <span className="absolute inset-0 rounded-full bg-cyan-400/40 animate-pulse-ring" />
          <span className="relative flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-ink-950 font-bold pl-3.5 pr-5 py-3.5 rounded-full shadow-lg shadow-cyan-500/30 transition">
            <MessageSquare className="w-5 h-5" />
            <span className="text-sm">Ask USI</span>
          </span>
        </button>
      )}

      {/* Panel */}
      {open && (
        <div data-testid="chat-panel" className="fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[380px] h-[560px] max-h-[80vh] flex flex-col rounded-2xl border border-white/10 bg-ink-900 shadow-2xl shadow-black/50 overflow-hidden">
          {/* Header */}
          <div className="px-5 py-4 border-b border-white/10 bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="relative">
                <img src={COMPANY.logo} alt="USI Security" className="h-9 w-auto object-contain" />
                <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-ink-900" />
              </div>
              <div>
                <div className="font-display font-bold text-white text-sm">USI Security</div>
                <div className="font-mono text-[10px] uppercase tracking-wider text-emerald-400">Online · UK Team</div>
              </div>
            </div>
            <button
              data-testid="chat-close"
              onClick={() => setOpen(false)}
              className="p-1.5 text-slate-400 hover:text-white rounded-md hover:bg-white/5 transition"
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-3">
            {messages.map((m) => (
              <div key={m.id} data-testid={`chat-msg-${m.sender}`} className={`flex ${m.sender === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                  m.sender === "user"
                    ? "bg-cyan-500 text-ink-950 rounded-br-md font-medium"
                    : "bg-white/5 border border-white/10 text-slate-200 rounded-bl-md"
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white/5 border border-white/10 rounded-2xl rounded-bl-md px-4 py-3">
                  <div className="flex gap-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce" style={{animationDelay:'0ms'}} />
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce" style={{animationDelay:'150ms'}} />
                    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-bounce" style={{animationDelay:'300ms'}} />
                  </div>
                </div>
              </div>
            )}

            {messages.length <= 1 && (
              <div className="pt-2 space-y-2">
                <p className="text-[11px] font-mono uppercase tracking-wider text-slate-500">Quick prompts</p>
                {["Get a quote", "Tell me about K9 patrols", "We have an urgent need"].map((q) => (
                  <button
                    key={q}
                    data-testid={`chat-quick-${q}`}
                    onClick={() => quick(q)}
                    className="block w-full text-left text-xs px-3 py-2 rounded-md border border-white/10 hover:border-cyan-400/40 hover:bg-white/5 text-slate-300 transition"
                  >
                    {q}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={send} className="border-t border-white/10 p-3 flex gap-2 bg-ink-900">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 bg-white/5 border border-white/10 rounded-md px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:border-cyan-400 focus:outline-none focus:ring-1 focus:ring-cyan-400/50"
            />
            <button
              data-testid="chat-send"
              type="submit"
              disabled={!input.trim() || sending}
              className="px-3 py-2.5 rounded-md bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 text-ink-950 transition"
            >
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
};
